import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { motion } from 'framer-motion';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock, translateTemplateLevelText, translateTemplateText } from '@focuslee/shared-mini-app';
import { Page } from '../components/Page';
import { AchievementConditionsDisplay } from '../components/achievements/AchievementConditionsDisplay';
import { AchievementItem } from '../components/achievements/AchievementItem';
import { ExternalAchievementConditionDisplay } from '../components/achievements/ExternalAchievementConditionDisplay';
import { AchievementViewPageSkeleton } from '../components/achievements/skeleton/AchievementViewPageSkeleton';
import { CommonIcon } from '../components/common/CommonIcon';
import { getAchievementLevelProgress } from '../helpers/achievement';
import { formatAchievementDate } from '../helpers/datetime';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedAchievementWithFallback } from '../hooks/caching/achievement';
import { useCachedCategoryWithFallback } from '../hooks/caching/category';
import { achievementEditPath } from '../navigation/paths';

export const AchievementViewPage: FC = () => {
    const { t } = useTranslation();
    const { id: achievementId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { achievement, loading } = useCachedAchievementWithFallback(achievementId);
    const { category } = useCachedCategoryWithFallback(achievement?.categoryId);
    const achievementLevelProgress = achievement ? getAchievementLevelProgress(achievement) : null;
    const displayedAchievement = achievementLevelProgress ?? achievement;
    const [expandedConditions, setExpandedConditions] = useState(false);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleEditClick = useCallback(() => {
        if (!achievementId) return;
        const path = achievementEditPath.replace(':id', achievementId);
        navigate(path);
    }, [navigate, achievementId]);

    const isEditEnabled = !category?.system;

    if (loading)
        return <AchievementViewPageSkeleton />;
    if (!displayedAchievement) return null;

    const level = achievementLevelProgress?.achievementLevels?.[Math.max(achievementLevelProgress.currentLevel - 1, 0)];
    const levelIndex = achievementLevelProgress ? Math.max(achievementLevelProgress.currentLevel - 1, 0) : null;
    const achievementName = level
        ? translateTemplateLevelText(t, displayedAchievement.templateId, levelIndex, 'name', level.name)
        : translateTemplateText(t, displayedAchievement.templateId, 'name', displayedAchievement.name);
    const achievementDescription = level
        ? translateTemplateLevelText(t, displayedAchievement.templateId, levelIndex, 'description', level.description)
        : translateTemplateText(t, displayedAchievement.templateId, 'description', displayedAchievement.description ?? '');

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.back'), onClick: navigateBack, navigation: 'back' }}
                    main={{
                        text: t('actions.update'),
                        onClick: handleEditClick,
                        enabled: isEditEnabled,
                        bgColor: getButtonThemeColors(isEditEnabled).backgroundColor,
                    }}
                />
            }
        >
            <Stack height="100%">
                <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
                    <Text size={20}>{t('achievementEditor.view')}</Text>
                </Stack>
                <motion.div
                    key="view-content"
                    style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Stack paddingLeft="10px" paddingRight="10px" paddingTop="4px" gap={2}>
                        <Stack alignSelf="center" sx={{ py: 2 }}>
                            <Box sx={{
                                width: 100,
                                height: 140,
                                filter: displayedAchievement.achievedOn || (achievementLevelProgress?.currentLevel ?? 0) > 0 ? 'none' : 'grayscale(100%)'
                            }}>
                                <AchievementItem
                                    icon={displayedAchievement.icon}
                                    title=""
                                    rarity={displayedAchievement.rarity}
                                    timeAgo={displayedAchievement.achievedOn ? formatAchievementDate(displayedAchievement.achievedOn) : undefined}
                                    iconImageColor={level?.iconImageColor ?? displayedAchievement.iconImageColor}
                                    iconImageUrl={level?.iconImageUrl ?? displayedAchievement.iconImageUrl}
                                />
                            </Box>
                        </Stack>
                        
                        <Text style={{ fontSize: '20px', lineHeight: '20px' }}>{achievementName}</Text>
                        <Text style={{ color: 'var(--tgui--hint_color)' }}>{achievementDescription || t('achievementEditor.noDescription')}</Text>
                        
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" columnGap={1}>
                                <CommonIcon icon="star" type="filled" color="var(--tg-theme-text-color)" size={20} />
                                <Text>{t(`achievementEditor.rarities.${displayedAchievement.rarity}`)}</Text>
                            </Stack>
                            {level && <Box sx={{ px: 1, py: 0.25, borderRadius: 3, backgroundColor: 'var(--tgui--button_color)' }}>
                                <Text weight="2" style={{ color: 'var(--tgui--button_text_color)', fontSize: '12px' }}>{t('achievementEditor.level', { count: achievementLevelProgress?.currentLevel })}</Text>
                            </Box>}
                        </Stack>

                        {achievementLevelProgress && <ExternalAchievementConditionDisplay achievement={achievementLevelProgress} />}

                        {!achievementLevelProgress && <AchievementConditionsDisplay
                            achievement={displayedAchievement}
                            expanded={expandedConditions}
                            setExpanded={setExpandedConditions}
                        />}
                    </Stack>
                </motion.div>
            </Stack>
        </Page>
    );
};
